import React, { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { CampaignItemIcon, ChevronIcon, TrashIcon, PlusIcon } from '../icons';
import ConfirmModal from '../../SprintDetail/FlowchartView/components/ConfirmModal';
import IconPicker from '../../SprintDetail/shared/IconPicker';
import { renderIcon } from '../../SprintDetail/FlowchartView/utils/renderIcon';
import { useItemEditing, useContextMenu, useTreeItemDragDrop } from '../hooks';
import { getEffectiveParentId } from '../utils/orphanDetection';

/** Same step as `../utils/treeIndent` — kept local so lint sees the binding. */
const sidebarTreeIndent = (depth = 0) => (depth > 0 ? 8 + depth * 8 : 8);

/**
 * SprintTreeItem Component
 * Renders a campaign item with optional nested children
 * Supports drag-and-drop to:
 * - Nest campaigns inside each other (drop on middle of item)
 * - Reorder campaigns (drop on top/bottom edge of item)
 * - Nest into parent when dropping between nested items
 */
const SprintTreeItem = ({
  sprint,
  nestedSprints,
  allSprints,
  selectedSprintId,
  onSelectSprint,
  onRenameSprint,
  onSprintIconChange,
  onDeleteSprint,
  onNestCampaign,
  onReorderCampaign,
  onReorderNestedCampaign,
  onCreateNestedSprint,
  onCreateTemplateFromCampaign,
  onOpenInNewTab,
  depth = 0,
  expandedSprints,
  onToggleExpand,
  draggedSprintId,
  onDragStart: parentDragStart,
  onDragEnd: parentDragEnd,
  missionDropTargetSprintId = null,
  index,
  parentSprintId = null,
  siblingOrder = null, // Array of sibling IDs in display order (passed from parent)
}) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showIconPicker, setShowIconPicker] = useState(false);
  const [iconPickerPosition, setIconPickerPosition] = useState(null);
  
  const iconRef = useRef(null);
  const itemRef = useRef(null);

  // Get children and sort by parent's childOrder (display order only).
  // Placement follows the mission-link invariant (same as Flow).
  const unsortedChildren = nestedSprints.filter(
    (ns) => getEffectiveParentId(ns, allSprints) === sprint.id
  );
  const childOrder = sprint.childOrder || [];
  const childSprints = [...unsortedChildren].sort((a, b) => {
    const indexA = childOrder.indexOf(a.id);
    const indexB = childOrder.indexOf(b.id);
    // Items in childOrder come first, in order; items not in childOrder come last
    if (indexA === -1 && indexB === -1) return 0; // Both not in order, keep original
    if (indexA === -1) return 1; // a not in order, b is, so b comes first
    if (indexB === -1) return -1; // b not in order, a is, so a comes first
    return indexA - indexB; // Both in order, sort by index
  });
  const hasChildren = childSprints.length > 0;
  const isExpanded = expandedSprints.has(sprint.id);
  const isSelected = selectedSprintId === sprint.id;
  const displayProgress = sprint.progress;
  const hasIcon = !!sprint.icon;

  // --- Custom Hooks ---
  
  const {
    isEditing,
    editValue,
    inputRef,
    startEditing,
    saveEdit,
    handleKeyDown,
    handleChange,
  } = useItemEditing(sprint.name, (newName) => onRenameSprint?.(sprint.id, newName));

  const { contextMenu, openContextMenu, closeContextMenu } = useContextMenu();

  const {
    isDragOver,
    reorderPosition,
    isBeingDragged,
    handleDragStart,
    handleDragEnd,
    handleDragOver,
    handleDragLeave,
    handleDrop,
  } = useTreeItemDragDrop({
    sprint,
    allSprints,
    childSprints,
    siblingOrder, // Pass the sibling order for nested reordering
    depth,
    parentSprintId,
    draggedSprintId,
    onNestCampaign,
    onReorderCampaign,
    onReorderNestedCampaign,
    onDragStart: parentDragStart,
    onDragEnd: parentDragEnd,
    itemRef,
  });

  // --- Event Handlers ---

  const handleRename = () => {
    closeContextMenu();
    startEditing();
  };

  const handleDelete = () => {
    closeContextMenu();
    setShowDeleteModal(true);
  };

  const confirmDelete = () => {
    setShowDeleteModal(false);
    onDeleteSprint?.(sprint.id);
  };

  const handleIconClick = (e) => {
    e.stopPropagation();
    if (iconRef.current) {
      const rect = iconRef.current.getBoundingClientRect();
      setIconPickerPosition({
        x: rect.right + 8,
        y: rect.top,
      });
    }
    setShowIconPicker(true);
  };

  const handleIconChange = (newIcon) => {
    onSprintIconChange?.(sprint.id, newIcon);
    // Don't close picker - let user set both icon and color
  };

  const handleCreateNestedSprint = (e) => {
    e.stopPropagation();
    onCreateNestedSprint?.(sprint.id);
  };

  const handleCreateTemplateFromCampaign = () => {
    closeContextMenu();
    onCreateTemplateFromCampaign?.(sprint.id);
  };

  const handleOpenInNewTab = () => {
    closeContextMenu();
    onOpenInNewTab?.(sprint, 'campaign');
  };

  const indentPx = sidebarTreeIndent(depth);

  const isMissionDropTarget = missionDropTargetSprintId === sprint.id;

  // Build class names
  const itemClassNames = [
    'sidebar__item',
    isSelected && 'sidebar__item--selected',
    depth > 0 && 'sidebar__item--nested',
    hasChildren && 'sidebar__item--has-children',
    displayProgress === 100 && 'sidebar__item--complete',
    (isDragOver || isMissionDropTarget) && 'sidebar__item--drag-over',
    isBeingDragged && 'sidebar__item--dragging',
  ].filter(Boolean).join(' ');

  return (
    <div className="sidebar__tree-item">
      {/* Reorder indicator - above */}
      {reorderPosition === 'above' && (
        <div className="sidebar__reorder-indicator sidebar__reorder-indicator--above" />
      )}
      
      <div
        ref={itemRef}
        className={itemClassNames}
        data-sidebar-campaign-id={sprint.id}
        data-sidebar-is-template="false"
        onClick={() => !isEditing && onSelectSprint?.(sprint)}
        onContextMenu={openContextMenu}
        style={{ paddingLeft: `${indentPx}px` }}
        draggable={!isEditing}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <div className="sidebar__item-row">
          {/* Icon container - shows custom icon or default */}
          <span 
            ref={iconRef}
            className={`sidebar__item-icon-container ${hasIcon ? 'sidebar__item-icon-container--custom' : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              handleIconClick(e);
            }}
            title={hasIcon ? "Click to change icon" : "Click to add icon"}
          >
            <span className="sidebar__item-icon">
              {hasIcon ? (
                renderIcon(sprint.icon, { 
                  size: '14px',
                  fallback: <CampaignItemIcon size={12} />
                })
              ) : (
                <CampaignItemIcon size={12} />
              )}
            </span>
          </span>
          
          {/* Content wrapper - contains label and progress bar */}
          <div className="sidebar__item-content">
            <div className="sidebar__item-label-row">
              {isEditing ? (
                <input
                  ref={inputRef}
                  type="text"
                  className="sidebar__item-input"
                  value={editValue}
                  onChange={handleChange}
                  onBlur={saveEdit}
                  onKeyDown={handleKeyDown}
                  onClick={(e) => e.stopPropagation()}
                />
              ) : (
                <span className="sidebar__item-label">{sprint.name}</span>
              )}
              
              {/* Action buttons container - shown on hover */}
              <span className="sidebar__item-actions">
                {/* Add nested campaign button */}
                <span 
                  className="sidebar__item-action-btn"
                  onClick={handleCreateNestedSprint}
                  title="Add nested campaign"
                >
                  <PlusIcon size={9} />
                </span>
                
                {/* Chevron for expandable items */}
                {hasChildren && (
                  <span 
                    className="sidebar__item-chevron"
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggleExpand(sprint.id);
                    }}
                  >
                    <ChevronIcon size={10} expanded={isExpanded} />
                  </span>
                )}
              </span>
            </div>
            
            {/* Progress bar - aligned with label, not icon */}
            {!isEditing && (
              <div 
                className="sidebar__item-progress-bar"
                style={{ 
                  '--progress': `${displayProgress || 0}%`,
                  '--progress-num': displayProgress || 1
                }}
              />
            )}
          </div>
        </div>
      </div>

      {/* Reorder indicator - below */}
      {reorderPosition === 'below' && (
        <div className="sidebar__reorder-indicator sidebar__reorder-indicator--below" />
      )}

      {/* Context Menu */}
      {contextMenu && createPortal(
        <div 
          className="sidebar__context-menu"
          style={{ left: contextMenu.x, top: contextMenu.y }}
        >
          <button className="sidebar__context-menu-item" onClick={handleOpenInNewTab}>
            Open in new tab
          </button>
          <div className="sidebar__context-menu-divider" />
          <button className="sidebar__context-menu-item" onClick={handleRename}>
            Rename
          </button>
          <button 
            className="sidebar__context-menu-item" 
            onClick={() => {
              closeContextMenu();
              setIconPickerPosition({ x: contextMenu.x, y: contextMenu.y });
              setShowIconPicker(true);
            }}
          >
            {hasIcon ? 'Change Icon' : 'Add Icon'}
          </button>
          <button className="sidebar__context-menu-item" onClick={handleCreateTemplateFromCampaign}>
            Create template from "{sprint.name}"
          </button>
          <div className="sidebar__context-menu-divider" />
          <button className="sidebar__context-menu-item sidebar__context-menu-item--danger" onClick={handleDelete}>
            <TrashIcon size={12} />
            Move to Trash
          </button>
        </div>,
        document.body
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={showDeleteModal}
        title="Move to Trash"
        message={`Move "${sprint.name}" to trash? You can restore it later from the Trash.`}
        confirmLabel="Move to Trash"
        cancelLabel="Cancel"
        confirmVariant="warning"
        icon="🗑"
        onConfirm={confirmDelete}
        onCancel={() => setShowDeleteModal(false)}
      />

      {/* Icon Picker */}
      {showIconPicker && (
        <IconPicker
          currentIcon={sprint.icon}
          onIconChange={handleIconChange}
          onIconDeleted={() => handleIconChange(null)}
          onClose={() => setShowIconPicker(false)}
          position={iconPickerPosition}
        />
      )}
      
      {/* Nested Children */}
      {hasChildren && isExpanded && (
        <div className="sidebar__tree-children">
          {childSprints.map((child, idx) => (
            <SprintTreeItem
              key={child.id}
              sprint={child}
              nestedSprints={nestedSprints}
              allSprints={allSprints}
              selectedSprintId={selectedSprintId}
              onSelectSprint={onSelectSprint}
              onRenameSprint={onRenameSprint}
              onSprintIconChange={onSprintIconChange}
              onDeleteSprint={onDeleteSprint}
              onNestCampaign={onNestCampaign}
              onReorderCampaign={onReorderCampaign}
              onReorderNestedCampaign={onReorderNestedCampaign}
              onCreateNestedSprint={onCreateNestedSprint}
              onCreateTemplateFromCampaign={onCreateTemplateFromCampaign}
              onOpenInNewTab={onOpenInNewTab}
              depth={depth + 1}
              expandedSprints={expandedSprints}
              onToggleExpand={onToggleExpand}
              draggedSprintId={draggedSprintId}
              onDragStart={parentDragStart}
              onDragEnd={parentDragEnd}
              missionDropTargetSprintId={missionDropTargetSprintId}
              index={idx}
              parentSprintId={sprint.id}
              siblingOrder={childSprints.map(c => c.id)} // Pass current display order to children
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default SprintTreeItem;
